import { now } from "@/helpers/time";
import {
  Box,
  Button,
  Card,
  Circle,
  Grid,
  HStack,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { Dayjs } from "dayjs";
import { useState } from "react";
import { LuArrowDown, LuArrowRight } from "react-icons/lu";
import { ProtectNavigation } from "../types";
import KeyDatesCalendar from "./KeyDatesCalendar";
import { downloadMonthIcs } from "./calendarFile";
import {
  DatedDeadline,
  PALOMMA_PAYMENTS,
  PALOMMA_PAYMENT_RULE,
  datedDeadlines,
  deadlineRule,
  palommaPaymentDate,
} from "./deadlines";

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text
    fontSize="xs"
    fontFamily="mono"
    letterSpacing="widest"
    textTransform="uppercase"
    color="gray.600"
  >
    {children}
  </Text>
);

const SectionCard = ({ children }: { children: React.ReactNode }) => (
  <Card.Root
    borderRadius="xl"
    border="none"
    boxShadow="rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
  >
    <Card.Body p="5" gap={4}>
      {children}
    </Card.Body>
  </Card.Root>
);

const Banner = ({
  deadline,
  today,
  onAction,
}: {
  deadline: DatedDeadline;
  today: Dayjs;
  onAction: () => void;
}): JSX.Element => {
  const daysLeft = deadline.date.diff(today.startOf("day"), "day");
  const remaining =
    daysLeft === 0
      ? "Hoy es el último día"
      : daysLeft === 1
      ? "Queda 1 día"
      : `Quedan ${daysLeft} días`;

  return (
    <HStack
      bg="orange.50"
      borderWidth="1px"
      borderColor="orange.200"
      borderRadius="xl"
      p="5"
      gap={4}
      flexShrink={0}
    >
      <Circle size="12" bg="orange.600" color="white" fontWeight={700}>
        {daysLeft}
      </Circle>
      <Stack gap={1} flex="1">
        <Text fontWeight={700} fontSize="lg">
          {remaining} para {deadline.bannerTitle} de {today.format("MMMM")}
        </Text>
        <Text color="gray.700">{deadline.description}</Text>
      </Stack>
      <Button colorPalette="orange" onClick={onAction}>
        {deadline.action}
      </Button>
    </HStack>
  );
};

const DeadlineRow = ({
  deadline,
  isActive,
  onAction,
}: {
  deadline: DatedDeadline;
  isActive: boolean;
  onAction: () => void;
}): JSX.Element => (
  <Grid
    gridColumn="1 / -1"
    templateColumns="subgrid"
    alignItems="center"
    borderWidth="1px"
    borderColor={isActive ? "palomma.500" : "gray.200"}
    bg={isActive ? "palomma.50" : "white"}
    borderRadius="lg"
    p="4"
  >
    <Stack
      gap={0}
      align="center"
      minW="14"
      pr={5}
      borderRightWidth="1px"
      borderColor="gray.200"
    >
      <Text
        fontSize="3xl"
        fontWeight={700}
        lineHeight="1.2"
        color={isActive ? "palomma.600" : undefined}
      >
        {deadline.date.date()}
      </Text>
      <Text fontSize="xs" color="gray.600">
        {deadline.date.format("dddd")}
      </Text>
    </Stack>
    <Stack gap={1}>
      <Text fontWeight={600} fontSize="lg">
        {deadline.title}
      </Text>
      <Text color="gray.600">{deadline.description}</Text>
      <Text fontSize="sm" color="gray.500">
        {deadlineRule(deadline.day)}
      </Text>
    </Stack>
    <Button
      variant={isActive ? "solid" : "outline"}
      colorPalette="palomma"
      onClick={onAction}
    >
      {deadline.action}
    </Button>
  </Grid>
);

type Props = { onNavigate: (params: ProtectNavigation) => void };

const KeyDatesTab = ({ onNavigate }: Props): JSX.Element => {
  const today = now();
  const [showCalendar, setShowCalendar] = useState(false);
  const deadlines = datedDeadlines(today);
  const active = deadlines.find(
    (deadline) => !today.isAfter(deadline.date, "day")
  );
  const month = today.format("MMMM");
  const paymentDate = palommaPaymentDate(today);

  if (showCalendar)
    return (
      <KeyDatesCalendar today={today} onBack={() => setShowCalendar(false)} />
    );

  return (
    <Stack gap={5} flex="1" minH={0} overflowY="auto">
      <Text fontSize="2xl" fontWeight={700} flexShrink={0}>
        Fechas clave de {month}
      </Text>

      {active && (
        <Banner
          deadline={active}
          today={today}
          onAction={() => onNavigate(active.params)}
        />
      )}

      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={5}>
        <SectionCard>
          <HStack justify="space-between">
            <SectionLabel>Lo que tú haces</SectionLabel>
            <SectionLabel>{deadlines.length} fechas</SectionLabel>
          </HStack>
          <Grid templateColumns="auto 1fr auto" columnGap={5} rowGap={3}>
            {deadlines.map((deadline) => (
              <DeadlineRow
                key={deadline.id}
                deadline={deadline}
                isActive={deadline === active}
                onAction={() => onNavigate(deadline.params)}
              />
            ))}
          </Grid>
        </SectionCard>

        <Stack gap={5}>
          <SectionCard>
            <HStack justify="space-between">
              <SectionLabel>Lo que Palomma hace</SectionLabel>
              <SectionLabel>No requiere acción</SectionLabel>
            </HStack>
            <Stack gap={4} separator={<Separator />}>
              {PALOMMA_PAYMENTS.map((payment) => (
                <HStack key={payment.description} align="start" gap={3}>
                  <Circle size="2.5" bg="green.500" mt={2} />
                  <Stack gap={0}>
                    <Text fontWeight={600} fontSize="lg">
                      {paymentDate.format("D [de] MMMM")}
                    </Text>
                    <Text color="gray.600">{payment.description}</Text>
                  </Stack>
                </HStack>
              ))}
            </Stack>
            <Text fontSize="sm" color="gray.500">
              {PALOMMA_PAYMENT_RULE}
            </Text>
          </SectionCard>

          <SectionCard>
            <SectionLabel>Que no se te pase</SectionLabel>
            <Button
              variant="subtle"
              colorPalette="palomma"
              justifyContent="space-between"
              size="lg"
              onClick={() => downloadMonthIcs(today, now())}
            >
              Agregar los cortes a mi calendario
              <LuArrowDown />
            </Button>
            <Box>
              <Button
                variant="plain"
                colorPalette="palomma"
                px={0}
                onClick={() => setShowCalendar(true)}
              >
                Ver el mes completo en calendario
                <LuArrowRight />
              </Button>
            </Box>
          </SectionCard>
        </Stack>
      </Grid>
    </Stack>
  );
};

export default KeyDatesTab;
